import React from 'react';
import {
  Divider,
  IconButton,
  Typography,
  Button,
  Grid,
  PaperProps,
  Skeleton,
  Paper,
} from '@mui/material';
import BugReportIcon from '@mui/icons-material/BugReport';
import clsx from 'clsx';
import {Box} from '@mui/system';
import {useDebugContext} from '../../contexts/DebugProvider';
import {SaltusAny} from '../../utils/SaltusAny';
import theme from '../../theme/Theme';

const classes = {
  root: {
    paddingTop: 0,
    paddingLeft: 0,
    paddingRight: 0,
    paddingBottom: theme.spacing(1),
    display: 'flex',
    flexDirection: 'column',
  },

  rightAligned: {
    float: 'right',
  },

  title: {
    minHeight: '2.5rem',
    paddingLeft: theme.spacing(1),
    display: 'flex',
    flexDirection: 'row',
    alignContent: 'center',
  },

  titleText: {
    ...theme.typography.button,
    color: theme.palette.text.primary,
    fontWeight: 500,
    verticalAlign: 'middle',
    fontSize: '1rem',
    lineHeight: 2.1,
  },

  divider: {
    marginBottom: theme.spacing(1),
  },
};

export function PanelTitle(props: SaltusAny) {
  const {title, debugClickHandler} = props;

  const {debug} = useDebugContext();

  const debugComponents = (
    <div>
      <IconButton
        aria-label="open-panel-debug-dialog"
        size="small"
        disabled={!debugClickHandler}
        onClick={() => debugClickHandler()}
      >
        <BugReportIcon />
      </IconButton>
    </div>
  );

  return (
    <div
      style={{
        minHeight: '2.5rem',
        paddingLeft: theme.spacing(1),
        display: 'flex',
        flexDirection: 'row',
        alignContent: 'center',
      }}
    >
      <Typography sx={classes.titleText}>{title}</Typography>
      <div style={{flexGrow: 1}} />
      {debug?.panels && debugComponents}
    </div>
  );
}

type PanelProps = PaperProps & {
  title?: string;
  loading?: boolean;
  height?: number | string;
  debugClickHandler?: () => SaltusAny | null;
  showButton?: boolean;
  buttonOnClick?: () => SaltusAny;
  buttonTitle?: React.ReactNode;
  className?: string;
  color?: string;
  childrenContainerStyle?: React.CSSProperties;
};

export const Panel: React.FC<PanelProps> = (props: PanelProps) => {
  const {
    title = '',
    children,
    loading = false,
    height,
    debugClickHandler,
    showButton,
    buttonOnClick = () => {},
    buttonTitle = '',
    className = '',
    color,
    style = {},
    sx,
    childrenContainerStyle = {},
    ...newProps
  } = props;

  if (loading) {
    return (
      <Skeleton
        animation="wave"
        sx={classes.root}
        variant="rectangular"
        height={height}
      />
    );
  }

  return (
    // eslint-disable-next-line react/jsx-props-no-spreading
    <Paper
      className={clsx(className, 'panel')}
      {...newProps}
      sx={{
        paddingTop: 0,
        paddingLeft: 0,
        paddingRight: 0,
        paddingBottom: theme.spacing(1),
        display: 'flex',
        flexDirection: 'column',
        ...style,
        ...sx,
        height,
      }}
    >
      <Grid
        container
        style={
          color
            ? {
                width: `100%`,
                background: theme.palette.grey[100],
              }
            : {}
        }
        direction="row"
      >
        <Grid
          style={
            color
              ? {
                  borderTop: `3px solid ${color}`,
                  position: 'relative',
                }
              : {}
          }
          item
          xs
        >
          {title && <PanelTitle title={title} debugClickHandler={debugClickHandler} />}
        </Grid>
        <Grid
          item
          style={
            color
              ? {
                  borderTop: `3px solid ${color}`,
                  position: 'relative',
                  paddingTop: '3px',
                  paddingRight: '1rem',
                }
              : {
                  marginTop: '1px',
                  marginRight: '1rem',
                }
          }
        >
          {showButton && (
            <Button
              size="small"
              sx={classes.rightAligned}
              onClick={buttonOnClick}
              variant="contained"
              color="secondary"
            >
              {buttonTitle}
            </Button>
          )}
        </Grid>
      </Grid>
      {title && !color && <Divider sx={classes.divider} />}
      <Box
        sx={{
          paddingTop: theme.spacing(1.5),
          paddingLeft: theme.spacing(1.5),
          paddingRight: theme.spacing(1.5),
          paddingBottom: theme.spacing(1.5),
        }}
        style={childrenContainerStyle}
      >
        {children}
      </Box>
    </Paper>
  );
};
